import { useEffect, useRef, useState } from 'react'
import { Box, Slide } from '@mui/material'
import WifiOffRoundedIcon from '@mui/icons-material/WifiOffRounded'
import CloudDoneRoundedIcon from '@mui/icons-material/CloudDoneRounded'
import { useTranslation } from 'react-i18next'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'

/**
 * Global connectivity indicator. Renders a fixed banner at the top of the
 * viewport whenever the browser goes offline, and briefly confirms when the
 * connection is restored. Mounted once at the app root so it covers every
 * route, including the public respondent journey and pages that load media
 * straight from S3.
 */
export default function OfflineBanner() {
  const online = useOnlineStatus()
  const { t } = useTranslation('common')
  const [showReconnected, setShowReconnected] = useState(false)
  // Avoid flashing "reconnected" on first mount when we were never offline.
  const wasOffline = useRef(false)

  useEffect(() => {
    if (!online) {
      wasOffline.current = true
      setShowReconnected(false)
      return
    }
    if (wasOffline.current) {
      wasOffline.current = false
      setShowReconnected(true)
      const timer = window.setTimeout(() => setShowReconnected(false), 3000)
      return () => window.clearTimeout(timer)
    }
  }, [online])

  const visible = !online || showReconnected

  return (
    <Slide direction="down" in={visible} mountOnEnter unmountOnExit>
      <Box
        role="status"
        aria-live="polite"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.snackbar + 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          px: 2,
          py: 1,
          fontSize: 14,
          fontWeight: 600,
          color: '#fff',
          bgcolor: online ? 'success.main' : 'error.main',
          boxShadow: 3,
        }}
      >
        {online ? (
          <CloudDoneRoundedIcon fontSize="small" />
        ) : (
          <WifiOffRoundedIcon fontSize="small" />
        )}
        <span>{online ? t('online') : t('offline')}</span>
      </Box>
    </Slide>
  )
}
